
import _ from 'lodash'
import { applyUsageFormRules } from '@/api/OfflineFormSchemas'
import { isUsageRunningAt, usageOnAt, parseUsageDateTime } from '@/api/usageTime'

const DETAIL_FIELDS = ['producer', 'farmunit', 'diversionPoint']

export default {
  name: 'CreateUsage',
  props: {
    usageData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      saving: false,
      open: true,
      active: {},
      turnoff: false,
      producer: null,
      loading: false,
      refreshingUsageState: false,
      usageStateRefreshFailed: false
    }
  },
  computed: {
    page () {
      return this.$store.getters['sectionSchemas/findbyname']('usages')
    },
    schemas () {
      return this.page.schemas
    },
    formSchema () {
      const schema = _.cloneDeep(this.schemas.find(s => s.key === 'usages_form'))

      if (this.turnoff) {
        schema.headers = schema.headers.filter(h => !h.options.readonly_on_turnoff)
      }

      schema.headers = schema.headers
        .filter(h => !DETAIL_FIELDS.includes(h.field))
        .map((header) => {
          if (header.field === 'order_details') {
            header.options.propbindings.class = 'usage-section-heading'
            header.options.propbindings.text = 'Usage Record Details'
          }

          return header
        })

      return applyUsageFormRules(schema)
    },
    preventSubmit () {
      return this.refreshingUsageState || this.usageStateRefreshFailed || this.duplicateRunningUsage
    },
    hideUsageSummary () {
      return this.refreshingUsageState || this.usageStateRefreshFailed
    },
    duplicateRunningUsage () {
      return !this.turnoff &&
        !this.active.id &&
        !this.active.flowUpdate &&
        !this.isEmpty(this.active.diversionPoint) &&
        Boolean(this.runningUsageForDiversionPoint(this.active.diversionPoint))
    },
    runningUsageLabel () {
      return this.runningUsage ? `#${this.runningUsage.id}` : ''
    },
    runningUsage () {
      return this.runningUsageForDiversionPoint(this.active.diversionPoint)
    }
  },
  async mounted () {
    this.setActive()
    await this.refreshUsageState()
    this.producer = this.currentProducer()
  },
  methods: {
    async refreshUsageState () {
      if (!this.shouldRefreshUsageState()) {
        return
      }

      this.refreshingUsageState = true
      this.usageStateRefreshFailed = false
      try {
        const repository = this.$repositories?.usages || this.$store.$repositories?.usages
        const response = await repository.index({
          waterYear: this.$store.state.system.waterYear,
          include: 'farmunit,ditch,user,diversionPoint',
          'filter_exact[diversion_point_id]': this.relationId(this.active.diversionPoint)
        })
        const payload = response && response.data ? response.data : response

        if (response?.error || response?.isAxiosError) {
          throw new Error('Unable to refresh usage state')
        }

        if (Array.isArray(payload)) {
          await this.$store.dispatch('usages/subListMerge', payload)
          this.$store.commit('usages/SET_FETCH_DATE')
        } else {
          throw new Error('Unexpected usage refresh response')
        }
      } catch (error) {
        this.usageStateRefreshFailed = true
        this.showErrorSnack('Unable to verify the latest water status for this turnout. Refresh before saving this record.')
      } finally {
        this.refreshingUsageState = false
      }
    },
    shouldRefreshUsageState () {
      if (
        this.turnoff ||
        this.active.id ||
        this.active.flowUpdate ||
        this.isEmpty(this.active.diversionPoint) ||
        this.usageStateRecentlyVerified()
      ) {
        return false
      }

      const online = this.$nuxt?.isOnline === true ||
        (this.$nuxt?.isOffline !== true && typeof navigator !== 'undefined' && navigator.onLine)
      const repository = this.$repositories?.usages || this.$store.$repositories?.usages

      return Boolean(online && repository)
    },
    usageStateRecentlyVerified () {
      const latestFetch = this.$store.state.usages.latest_fetch
      if (!latestFetch) {
        return false
      }

      const fetchedAt = new Date(latestFetch.replace(' ', 'T'))
      return !Number.isNaN(fetchedAt.getTime()) && (new Date() - fetchedAt) < 60000
    },
    async success () {
      try {
        if (this.objHas(this.usageData, 'flowUpdate') && this.usageData.flowUpdate === true) {
          const backendClosedOldUsage = navigator.onLine && this.numericOrderId(this.usageData.oldUsageId) !== null

          if (backendClosedOldUsage) {
            // The store transaction already closed the old usage server-side;
            // mirror it locally so running-water checks update immediately.
            const oldId = this.numericOrderId(this.usageData.oldUsageId)
            const existingOld = this.$store.state.usages.all[oldId]
            if (existingOld && !existingOld.off_date) {
              this.$store.commit('usages/MERGE', {
                [oldId]: { ...existingOld, off_date: this.usageData.on_date, off_time: this.usageData.on_time }
              })
            }
          } else {
            const usageToClose = this.$store.getters['usages/find'](this.usageData.oldUsageId)
            if (usageToClose) {
              this.loading = true
              const closable = { ...usageToClose }
              delete closable.flowUpdate
              delete closable.oldUsageId
              const cleanFormData = this.onlyIds(closable)

              const obj = {
                id: this.usageData.oldUsageId,
                payload: { ...cleanFormData, off_date: this.usageData.on_date, off_time: this.usageData.on_time }
              }
              const results = await this.$store.dispatch('usages/update', obj)
              if (this.hasSubmitError(results)) {
                throw new Error(this.submitErrorMessage(results, 'Unable to close the previous running usage. The new usage was saved, but the old usage still needs attention.'))
              }
            }
          }
        }

        const wasCreate = !this.usageData?.id
        const tiedOrderId = wasCreate ? this.numericOrderId(this.active?.order_id) : null

        // The backend completed the tied order inside the usage-create
        // transaction; reflect it locally so unfulfilled lists update at once.
        if (tiedOrderId) {
          const existingOrder = this.$store.state.orders.all[tiedOrderId]
          if (existingOrder && !existingOrder.completed) {
            this.$store.commit('orders/MERGE', { [tiedOrderId]: { ...existingOrder, completed: true } })
          }
        }

        this.$emit('success', wasCreate ? { tiedOrderId } : undefined)
        this.$emit('closeUsageForm')
      } catch (error) {
        this.showErrorSnack(error?.message || 'Unable to finalize water usage changes. Please try again.')
      } finally {
        this.loading = false
      }
    },
    setActive () {
      const temp = { ...JSON.parse(JSON.stringify(this.$store.state.usages.bound)), ..._.cloneDeep(this.usageData) }

      if (temp.turnoff) {
        this.turnoff = true
      }

      delete temp.turnoff
      this.active = temp
      this.producer = this.currentProducer()
    },
    currentProducer () {
      return this.active.producer ? this.$store.getters['contacts/find'](this.active.producer) : null
    },
    emitUp (event) {
      if (event?.event === 'orderCompleted') {
        this.$emit('success')
        this.$emit('closeUsageForm')
      }
    },
    async submitUsageForm () {
      if (this.saving) {
        return
      }
      if (this.preventSubmit) {
        this.showErrorSnack(this.usageStateRefreshFailed
          ? 'Unable to verify the latest water status for this turnout. Refresh before saving this record.'
          : 'This turnout already has running water. Use Adjust or Turn Off instead.')
        return
      }

      // Backend enforces this too; catching it here saves the round trip and
      // gives the rider an instant, specific message.
      const onAt = parseUsageDateTime(this.active.on_date, this.active.on_time, '00:00:00')
      const offAt = this.active.off_date
        ? parseUsageDateTime(this.active.off_date, this.active.off_time, '00:00:00')
        : null
      if (onAt && offAt && offAt < onAt) {
        this.showErrorSnack('Off time must be after the on time — this delivery would end before it starts.')
        return
      }

      this.saving = true
      try {
        await this.$refs.usageForm?.submitForm()
      } finally {
        this.saving = false
      }
    },
    runningCfsForDiversionPoint (diversionPoint) {
      return this.$store.getters['usages/runningUsagesFor'](diversionPoint)
        .reduce((total, usage) => total + this.numeric(usage.flow), 0)
    },
    runningUsageForDiversionPoint (diversionPoint) {
      const running = this.$store.getters['usages/runningUsagesFor'](diversionPoint)
      return running
        .slice()
        .sort((a, b) => (usageOnAt(b) || 0) - (usageOnAt(a) || 0))[0] || null
    },
    isCurrentlyRunning (usage) {
      return isUsageRunningAt(usage)
    },
    relationId (value) {
      return value && typeof value === 'object' ? value.id : value
    },
    numericOrderId (value) {
      const number = Number(value)
      return value !== null && value !== undefined && value !== '' && Number.isInteger(number) ? number : null
    },
    numeric (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    hasSubmitError (results) {
      return !results || results.isAxiosError || this.objHas(results, 'error') || results?.response?.data?.errors
    },
    submitErrorMessage (results, fallback) {
      const responseErrors = results?.error?.data?.errors || results?.response?.data?.errors
      if (responseErrors && typeof responseErrors === 'object') {
        const firstError = _.flattenDeep(Object.values(responseErrors)).find(Boolean)
        if (firstError) {
          return firstError
        }
      }

      return results?.error?.data?.message ||
        results?.error?.message ||
        results?.response?.data?.message ||
        fallback
    }
  }
}
